import { SPLIT_TESTING_KEY, TRACKING_PROPERTIES_KEY, USER_ID_KEY } from '$lib/global/constants';

import loadAnalyticsWrapper, { updateSegmentTraitsKey } from '$lib/global/events/loadAnalytics';
import getQueryParameter from '$lib/global/helper/get-query-parameter';
import { handleAnonId } from '$lib/global/storage';
import { setTrackingProperty } from '$lib/helper';
import { embedOptions } from '$lib/Stores/stores';
import { clearUserAnswers } from '$lib/utils/userAnswerStorage';

import { nanoid } from 'nanoid/non-secure';
import { get } from 'svelte/store';

import { getNowISO } from './helper';
import { integrations as trackingIntegrations } from './integrations';
import { ensureAnalyticsStub, getTrackingTitles } from './tracking';
import { serializeForPipeline } from './TrackingContext';
import { TrackingSession } from './TrackingSession';
import { V3Strategy, V4Strategy } from './TrackingStrategy';
import { campaignLoad } from './v4';
import psProperties from './v4.ps-properties';
import { updateVisitorProperties } from './v4.visitor-properties';

import { trackPage } from './index';

import type { Field } from '$lib/types/Field';
import type { LayoutPageEntry } from '$lib/types/LayoutData';
import type { Page } from '$lib/types/Page';
import type { PageContext, PipelineContext } from './TrackingContext';
import type { TrackingStrategy } from './TrackingStrategy';

// ── Config type ──────────────────────────────────────────────────────────

export interface TrackingServiceConfig {
    campaignId: string;
    companyId: string;
    workspaceId: string;
    campaignVersionId: string;
    trackingVersion: string | undefined;
    preventTracking: boolean;
    analyticsLink: string;
}

// ── TrackingService ──────────────────────────────────────────────────────

export class TrackingService {
    // Identity
    private _userId: string;

    // Campaign config (immutable after construction)
    private readonly config: TrackingServiceConfig;

    // Page map from layout
    private readonly pages: LayoutPageEntry[];

    // Tracking titles (funnel-level, computed once from v4 fields)
    private _trackingTitles: Record<string, string> = {};

    // Query parameters (parsed once at init, reused on session regeneration)
    private _utm: Record<string, string> = {};
    private _custom: Record<string, string> = {};
    private _thirdParty: Record<string, string> = {};

    private pageContext: PageContext | null = null;

    // Page view dedup (inlined from TrackingPageView)
    private prevPageId: string | null = null;
    private prevPageName: string | null = null;

    // Bootstrap guard (inlined from TrackingInit)
    private _bootstrapped = false;

    // Composites
    private session: TrackingSession;

    // Strategy
    private strategy: TrackingStrategy | null = null;

    // A/B variant selection (null = no variant in funnel)
    private _variant: 'A' | 'B' | null = null;

    constructor(config: TrackingServiceConfig, pages: LayoutPageEntry[]) {
        this.config = config;
        this.pages = pages;

        // Resolve userId directly from localStorage (matches handleAnonId logic)
        let userId = window.localStorage.getItem(USER_ID_KEY);

        if (!userId) {
            userId = nanoid();
            window.localStorage.setItem(USER_ID_KEY, userId);
        }
        this._userId = userId;

        // Build composites
        this.session = new TrackingSession();
    }

    // ── Public API: bootstrap (one-time init, called from layout) ───────

    bootstrap(fields: Field[]): void {
        if (this._bootstrapped) return;
        this._bootstrapped = true;

        this.strategy = this.config.trackingVersion === 'v3' ? new V3Strategy() : new V4Strategy();

        // Parse and store query parameters (once — SvelteKit soft nav preserves search params)
        const { utm, custom, thirdParty, additionalTrackingTitles } = getQueryParameter();
        this._utm = utm;
        this._custom = custom;
        this._thirdParty = thirdParty;

        // Build tracking titles from fields + query param names (v4 only)
        if (this.isV4()) {
            this._trackingTitles = {
                ...getTrackingTitles(fields),
                ...additionalTrackingTitles,
            };
        }

        // Safety net — ensure analytics stub exists (no-ops if already created)
        ensureAnalyticsStub();

        const analytics: any = window.analytics;

        if (!analytics.initialize) {
            void loadAnalyticsWrapper({
                companyId: this.config.companyId,
                integrations: { Perspective: !!trackingIntegrations.perspective },
                preventTracking: this.config.preventTracking,
                analyticsLink: this.config.analyticsLink,
            });
        }

        // Initialize A/B variant selection if the funnel has variant pages
        const hasVariant = this.pages.some((p) => p.isVariant);

        if (hasVariant) {
            const key = SPLIT_TESTING_KEY + this.config.campaignId;
            const stored = window.sessionStorage.getItem(key);

            if (stored === 'A' || stored === 'B') {
                this._variant = stored;
            } else {
                this._variant = Math.random() < 0.5 ? 'A' : 'B';
                window.sessionStorage.setItem(key, this._variant);
            }
        }
    }

    // ── Public API: handleRouteChange (called from layout afterNavigate) ─

    handleRouteChange(slug: string | undefined, pageData: PageContext): void {
        // Skip if slug doesn't match any known page (e.g. /consent-policy)
        if (slug && !this.pages.some((p) => p.slug === slug)) return;

        // Update page context BEFORE session resolution so that createNewSession
        // has the correct page data for initVisitorProperties (ps_start_slug, ps_start_page).
        this.pageContext = pageData;

        const isHomePage = this.resolveIsHomePage(slug);
        this.resolveSession(isHomePage);
        this.session.setCurrentPage(slug ?? '', isHomePage);
    }

    // ── Public API: trackPageTransition (called from Page.svelte) ────────

    trackPageTransition(pageData: Page): void {
        trackPage({
            pageName: pageData.pageName,
            tag: pageData.tag,
            campaignId: pageData.campaignId,
            pageSlug: pageData.pageSlug,
            isLastPage: pageData.isLastPage,
            pageId: pageData.pageId,
            fromPageId: this.prevPageId,
            fromPageName: this.prevPageName,
        });
        this.prevPageId = pageData.pageId;
        this.prevPageName = pageData.pageName;
    }

    // ── Page map helpers ────────────────────────────────────────────────

    private resolveIsHomePage(slug?: string): boolean {
        if (!slug) return true; // root route = homepage
        const entry = this.pages.find((p) => p.slug === slug);
        return entry?.isHomePage ?? false;
    }

    // ── Session resolution ───────────────────────────────────────────────

    private resolveSession(isHomePage: boolean): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        const hasUrlSessionId = urlSessionId !== null;

        // Soft nav — session already in memory
        if (this.session.getSessionId()) {
            if (hasUrlSessionId && urlSessionId !== this.session.getSessionId()) {
                this.createNewSession();
                return;
            }

            if (isHomePage && this.session.shouldRegenerateForHomepage()) {
                this.createNewSession();
                return;
            }

            // Keep current session (covers same urlSessionId or no urlSessionId)
            return;
        }

        // Hard load — try URL sessionId first
        if (hasUrlSessionId) {
            const peeked = TrackingSession.peekBySessionId(urlSessionId);

            if (
                !peeked || // No traits to restore OR
                (isHomePage && peeked.hasSeenAnyNonHomepage) // Regenerate for homepage if seen non-homepage
            ) {
                this.createNewSession(); // Fall through to createNewSession
                return;
            }

            this.session.tryRestoreFromSessionId(this.config.campaignId, urlSessionId);
            updateSegmentTraitsKey(this.sessionId);

            if (this.isV4()) {
                this.initVisitorProperties();
            }

            return;
        }

        // Hard load, no URL sessionId — try to restore from sessionStorage
        const stored = TrackingSession.peek(this.config.campaignId);

        if (stored) {
            if (isHomePage && stored.hasSeenAnyNonHomepage) {
                this.createNewSession();
                return;
            }

            // Restore the stored session into this.session
            this.session.tryRestore(this.config.campaignId);
            updateSegmentTraitsKey(this.sessionId);

            // Merge URL params into restored session's visitor properties (v4 only)
            if (this.isV4()) {
                this.initVisitorProperties();
            }

            return;
        }

        // Nothing to restore — first visit
        this.createNewSession();
    }

    private createNewSession(): void {
        this.session = new TrackingSession();
        this.session.create(this.config.campaignId);
        this.resetAnalytics();
        this.clearPersonalizationData();
        this.seedTrackingPropertiesFromUrl();

        // Seed visitor properties for the new session (v4 only).
        if (this.isV4()) {
            this.initVisitorProperties();
            campaignLoad(this.pageContext?.pageSlug || '', this.pageContext?.pageName || '');
        }
    }

    private clearPersonalizationData(): void {
        clearUserAnswers(this.config.campaignVersionId);
        window.sessionStorage.removeItem(`${TRACKING_PROPERTIES_KEY}${this.config.campaignId}`);
    }

    /**
     * Re-seed tracking properties from URL query parameters (ps_* / perspective_* and utm_*)
     * after session creation clears personalization data. This ensures that custom variables
     * passed via GET parameters survive across page navigations within the funnel.
     */
    private seedTrackingPropertiesFromUrl(): void {
        const { custom, utm } = getQueryParameter();

        Object.entries(custom).forEach(([key, value]) => {
            if (typeof value === 'string') {
                setTrackingProperty(key, value, this.config.campaignId);
            }
        });

        Object.entries(utm).forEach(([key, value]) => {
            if (typeof value === 'string') {
                setTrackingProperty(key, value, this.config.campaignId);
            }
        });
    }

    private initVisitorProperties(): void {
        const visitorProperties = this.session.getVisitorProperties();

        updateVisitorProperties({
            [psProperties.firstSeenAt]: visitorProperties[psProperties.firstSeenAt] || getNowISO(),
            [psProperties.startSlug]:
                visitorProperties[psProperties.startSlug] || this.pageContext?.pageSlug || '',
            [psProperties.startPage]:
                visitorProperties[psProperties.startPage] || this.pageContext?.pageName || '',
            ...this._utm,
            ...this._custom,
            ...this._thirdParty,
            [psProperties.isEmbed]: get(embedOptions).isEmbed ?? false,
            [psProperties.timezone]: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    private resetAnalytics(): void {
        if (window.analytics) {
            window.analytics.reset();
            handleAnonId();
            updateSegmentTraitsKey(this.sessionId);
        }
    }

    // ── Getters: identity ────────────────────────────────────────────────

    get sessionId(): string {
        return this.session.getSessionId();
    }

    get userId(): string {
        return this._userId;
    }

    // ── Getters: campaign config ─────────────────────────────────────────

    get campaignId(): string {
        return this.config.campaignId;
    }

    get companyId(): string {
        return this.config.companyId;
    }

    get workspaceId(): string {
        return this.config.workspaceId;
    }

    get campaignVersionId(): string {
        return this.config.campaignVersionId;
    }

    get trackingVersion(): string | undefined {
        return this.config.trackingVersion;
    }

    // ── Getters: page context ────────────────────────────────────────────

    get pageSlug(): string {
        return this.pageContext?.pageSlug ?? '';
    }

    get pageId(): string {
        return this.pageContext?.pageId ?? '';
    }

    get trackingTitles(): Record<string, string> {
        return this._trackingTitles;
    }

    get variant(): 'A' | 'B' | null {
        return this._variant;
    }

    resolveDisplayData(page: Page): Page {
        if (this._variant === 'B' && page.variantPageData) {
            return {
                ...(page.variantPageData as Page),
                inputMap: page.inputMap,
                pageIdSlugMap: page.pageIdSlugMap,
                context: page.context,
            };
        }
        return page;
    }

    get queryParams(): {
        utm: Record<string, string>;
        custom: Record<string, string>;
        thirdParty: Record<string, string>;
    } {
        return { utm: this._utm, custom: this._custom, thirdParty: this._thirdParty };
    }

    // ── Getters: session / visitor ───────────────────────────────────────

    get visitorProperties(): Partial<Record<string, string>> {
        return this.session.getVisitorProperties();
    }

    // ── Context methods ──────────────────────────────────────────────────

    getEventContext(): Partial<PipelineContext> {
        if (!this.pageContext) {
            return {};
        }
        return serializeForPipeline({
            sessionId: this.sessionId,
            userId: this._userId,
            config: { ...this.config, trackingTitles: this._trackingTitles },
            page: this.pageContext,
        });
    }

    // ── Version helpers ──────────────────────────────────────────────────

    isV3(): boolean {
        return this.config.trackingVersion === 'v3';
    }

    isV4(): boolean {
        return this.config.trackingVersion === 'v4';
    }

    // ── Composite accessors ──────────────────────────────────────────────

    getSession(): TrackingSession {
        return this.session;
    }

    getStrategy(): TrackingStrategy {
        if (!this.strategy) {
            throw new Error('TrackingStrategy not initialized — call bootstrap() first');
        }
        return this.strategy;
    }
}
